import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, TextField, MenuItem, Button, Alert, Table, TableHead,
  TableBody, TableRow, TableCell, CircularProgress, Chip, Paper, Grid,
  ToggleButton, ToggleButtonGroup, Divider,
} from '@mui/material';
import { EventAvailable, Assessment, Save, PictureAsPdf, GridOn } from '@mui/icons-material';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const API = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const today = () => new Date().toISOString().slice(0, 10);

// Take class- or subject-level attendance and view a per-reason/per-learner
// report. `targets` are the marking scopes (the teacher's own class + each
// subject class); `year`/`term` come from the Teacher Centre header.
export default function TeacherAttendance({ targets = [], year, term }) {
  const [targetKey, setTargetKey] = useState('');
  const [date, setDate] = useState(today());
  const [session, setSession] = useState('Session 1');
  const [reasons, setReasons] = useState([]);

  const [className, setClassName] = useState('');
  const [rows, setRows] = useState([]);           // { admNo, name, status, reason, remark }
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [showReport, setShowReport] = useState(false);
  const [range, setRange] = useState({ from: '', to: '' });
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);

  const target = useMemo(() => targets.find(t => t.key === targetKey), [targets, targetKey]);

  useEffect(() => {
    axios.get(`${API}/api/academics/attendance/reasons`, { headers: hdrs() })
      .then(({ data }) => setReasons(data)).catch(() => setReasons([]));
  }, []);

  useEffect(() => {
    if (!targetKey && targets.length) setTargetKey(targets[0].key);
  }, [targets, targetKey]);

  // Load the roster and merge any previously-saved sheet for this date/session,
  // so re-marking edits in place rather than starting blank.
  const loadRoster = useCallback(async () => {
    if (!target) return;
    setLoading(true); setError(''); setSuccess('');
    try {
      const params = { scope: target.scope, classCode: target.classCode,
        areaCode: target.areaCode || '', academicYear: year, term };
      const [{ data: roster }, { data: sheet }] = await Promise.all([
        axios.get(`${API}/api/academics/class-teacher/roster`, { headers: hdrs(), params }),
        axios.get(`${API}/api/academics/attendance/sheet`, { headers: hdrs(),
          params: { ...params, date, session } }),
      ]);
      setClassName(roster.className);
      const saved = {};
      (sheet?.entries || []).forEach(e => { saved[e.admNo] = e; });
      setRows((roster.learners || []).map(l => {
        const s = saved[l.admNo];
        return { admNo: l.admNo, name: l.name,
          status: s?.status || 'Present', reason: s?.reason || '', remark: s?.remark || '' };
      }));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load roster');
      setRows([]);
    } finally { setLoading(false); }
  }, [target, year, term, date, session]);

  useEffect(() => { loadRoster(); }, [loadRoster]);

  const setRow = (admNo, patch) =>
    setRows(rs => rs.map(r => r.admNo === admNo ? { ...r, ...patch } : r));

  const setStatus = (admNo, status) =>
    setRows(rs => rs.map(r => r.admNo === admNo
      ? { ...r, status, reason: status === 'Present' ? '' : r.reason } : r));

  const markAll = (status) =>
    setRows(rs => rs.map(r => ({ ...r, status, reason: status === 'Present' ? '' : r.reason })));

  const save = async () => {
    if (!target) return;
    setSaving(true); setError(''); setSuccess('');
    try {
      await axios.post(`${API}/api/academics/attendance`, {
        scope: target.scope, classCode: target.classCode,
        areaCode: target.areaCode || '', areaName: target.areaName || '',
        academicYear: year, term, date, session, entries: rows,
      }, { headers: hdrs() });
      setSuccess('Attendance saved');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save attendance');
    } finally { setSaving(false); }
  };

  const loadReport = async () => {
    if (!target) return;
    setReportLoading(true); setError('');
    try {
      const { data } = await axios.get(`${API}/api/academics/attendance/report`, {
        headers: hdrs(),
        params: { scope: target.scope, classCode: target.classCode,
          areaCode: target.areaCode || '', academicYear: year, term,
          from: range.from || undefined, to: range.to || undefined },
      });
      setReport(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load report');
    } finally { setReportLoading(false); }
  };

  const counts = useMemo(() => {
    const c = { Present: 0, Absent: 0, Late: 0 };
    rows.forEach(r => { c[r.status] = (c[r.status] || 0) + 1; });
    return c;
  }, [rows]);

  const reportTitle = () => {
    if (!report) return 'Attendance Report';
    const scope = report.scope === 'subject' ? ` · ${target?.areaName || ''}` : ' (whole class)';
    const rangeStr = (range.from || range.to) ? ` [${range.from || '…'} → ${range.to || '…'}]` : '';
    return `Attendance — ${report.className}${scope}, ${report.academicYear} Term ${report.term}${rangeStr}`;
  };
  const fileBase = () => `attendance-${(report?.className || 'class').replace(/\s+/g, '_')}${report?.scope === 'subject' ? '-' + (target?.areaCode || '') : ''}`;

  const exportExcel = () => {
    if (!report) return;
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([
      [reportTitle()], ['Sheets marked', report.totalSheets],
    ]), 'Summary');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(
      report.learners.map(l => ({
        'Adm No': l.admNo, Name: l.name, Present: l.present, Absent: l.absent, Late: l.late,
        'Absence %': l.absenceRate,
        'Top reasons': Object.entries(l.byReason).sort((a, b) => b[1] - a[1]).map(([r, n]) => `${r} (${n})`).join(', '),
      }))), 'Learners');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(
      report.reasons.map(r => ({ Reason: r.reason, Count: r.count }))), 'By Reason');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(
      report.byDate.map(d => ({ Date: d.date, Session: d.session, Present: d.present, Absent: d.absent, Late: d.late, 'Rate %': d.rate }))), 'By Date');
    XLSX.writeFile(wb, `${fileBase()}-${today()}.xlsx`);
  };

  const exportPdf = () => {
    if (!report) return;
    const doc = new jsPDF();
    doc.setFontSize(12);
    doc.text(reportTitle(), 14, 14);
    doc.setFontSize(9);
    doc.text(`Sheets marked: ${report.totalSheets}`, 14, 20);

    const affected = report.learners.filter(l => l.absent + l.late > 0);
    autoTable(doc, {
      startY: 25, styles: { fontSize: 8 }, headStyles: { fillColor: [21, 101, 192] },
      head: [['Learner', 'Present', 'Absent', 'Late', 'Abs %', 'Top reasons']],
      body: affected.length ? affected.map(l => [
        l.name, l.present, l.absent, l.late, `${l.absenceRate}%`,
        Object.entries(l.byReason).sort((a, b) => b[1] - a[1]).map(([r, n]) => `${r} (${n})`).join(', '),
      ]) : [['Full attendance — no absences', '', '', '', '', '']],
    });
    if (report.reasons.length) {
      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 6, styles: { fontSize: 8 }, headStyles: { fillColor: [123, 31, 162] },
        head: [['Absence reason', 'Count']],
        body: report.reasons.map(r => [r.reason, r.count]),
      });
    }
    doc.save(`${fileBase()}-${today()}.pdf`);
  };

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Box display="flex" gap={1.5} flexWrap="wrap" alignItems="center" mb={2}>
        <TextField select label="Class / Subject" value={targetKey} size="small"
          onChange={e => setTargetKey(e.target.value)} sx={{ minWidth: { xs: '100%', sm: 260 } }}>
          {targets.length === 0 && <MenuItem value="" disabled>No classes or subjects assigned</MenuItem>}
          {targets.map(t => <MenuItem key={t.key} value={t.key}>{t.label}</MenuItem>)}
        </TextField>
        <TextField label="Date" type="date" value={date} size="small"
          onChange={e => setDate(e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField label="Session" value={session} size="small"
          onChange={e => setSession(e.target.value)} sx={{ width: 130 }} />
        <Button variant="contained" startIcon={<Save />} onClick={save}
          disabled={saving || loading || rows.length === 0}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
        <Button variant="outlined" startIcon={<Assessment />}
          onClick={() => { setShowReport(s => !s); if (!report) loadReport(); }}>
          {showReport ? 'Hide Report' : 'View Report'}
        </Button>
      </Box>

      {target && (
        <Box display="flex" gap={1} flexWrap="wrap" alignItems="center" mb={1.5}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {className || target.classCode}{target.scope === 'subject' ? ` · ${target.areaName}` : ''}
          </Typography>
          <Chip size="small" color="success" label={`Present ${counts.Present}`} />
          <Chip size="small" color="error" label={`Absent ${counts.Absent}`} />
          <Chip size="small" color="warning" label={`Late ${counts.Late}`} />
          <Box flexGrow={1} />
          <Button size="small" onClick={() => markAll('Present')}>All Present</Button>
          <Button size="small" onClick={() => markAll('Absent')}>All Absent</Button>
        </Box>
      )}

      {loading ? <CircularProgress /> : rows.length === 0 ? (
        <Typography color="text.secondary">No learners on this roster.</Typography>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 720 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell><strong>#</strong></TableCell>
                <TableCell><strong>Adm No</strong></TableCell>
                <TableCell><strong>Name</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Reason</strong></TableCell>
                <TableCell><strong>Remark</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r, i) => (
                <TableRow key={r.admNo} hover
                  sx={{ bgcolor: r.status === 'Absent' ? '#fff5f5' : r.status === 'Late' ? '#fffaf0' : 'inherit' }}>
                  <TableCell>{i + 1}</TableCell>
                  <TableCell>{r.admNo}</TableCell>
                  <TableCell>{r.name}</TableCell>
                  <TableCell>
                    <ToggleButtonGroup size="small" exclusive value={r.status}
                      onChange={(_, v) => v && setStatus(r.admNo, v)}>
                      <ToggleButton value="Present" color="success" sx={{ py: 0.2, px: 1 }}>P</ToggleButton>
                      <ToggleButton value="Absent" color="error" sx={{ py: 0.2, px: 1 }}>A</ToggleButton>
                      <ToggleButton value="Late" color="warning" sx={{ py: 0.2, px: 1 }}>L</ToggleButton>
                    </ToggleButtonGroup>
                  </TableCell>
                  <TableCell>
                    <TextField select size="small" value={r.reason}
                      disabled={r.status === 'Present'} sx={{ minWidth: 150 }}
                      onChange={e => setRow(r.admNo, { reason: e.target.value })}>
                      <MenuItem value="">—</MenuItem>
                      {reasons.map(rc => <MenuItem key={rc} value={rc}>{rc}</MenuItem>)}
                    </TextField>
                  </TableCell>
                  <TableCell>
                    <TextField size="small" value={r.remark} placeholder="—"
                      onChange={e => setRow(r.admNo, { remark: e.target.value })} sx={{ minWidth: 140 }} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {showReport && (
        <Paper variant="outlined" sx={{ mt: 3, p: 2 }}>
          <Box display="flex" gap={1.5} flexWrap="wrap" alignItems="center" mb={2}>
            <Typography variant="h6" sx={{ mr: 1 }}>
              <EventAvailable sx={{ verticalAlign: 'middle', mr: 0.5 }} fontSize="small" />
              Attendance Report
            </Typography>
            <TextField label="From" type="date" size="small" value={range.from}
              onChange={e => setRange(r => ({ ...r, from: e.target.value }))} InputLabelProps={{ shrink: true }} />
            <TextField label="To" type="date" size="small" value={range.to}
              onChange={e => setRange(r => ({ ...r, to: e.target.value }))} InputLabelProps={{ shrink: true }} />
            <Button size="small" variant="contained" onClick={loadReport} disabled={reportLoading}>
              {reportLoading ? 'Loading…' : 'Run'}
            </Button>
            <Box flexGrow={1} />
            <Button size="small" variant="outlined" startIcon={<GridOn />}
              onClick={exportExcel} disabled={!report}>Excel</Button>
            <Button size="small" variant="outlined" startIcon={<PictureAsPdf />}
              onClick={exportPdf} disabled={!report}>PDF</Button>
          </Box>

          {reportLoading ? <CircularProgress /> : report ? (
            <Box>
              <Typography variant="caption" color="text.secondary">
                {report.totalSheets} sheet(s) marked for {report.className}
                {report.scope === 'subject' ? ' (subject)' : ''}
              </Typography>

              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={12} md={5}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Absences by reason</Typography>
                  {report.reasons.length === 0 ? (
                    <Typography color="text.secondary" variant="body2">No absences recorded.</Typography>
                  ) : (
                    <Table size="small">
                      <TableHead><TableRow sx={{ bgcolor: 'grey.100' }}>
                        <TableCell><strong>Reason</strong></TableCell>
                        <TableCell align="right"><strong>Count</strong></TableCell>
                      </TableRow></TableHead>
                      <TableBody>
                        {report.reasons.map(r => (
                          <TableRow key={r.reason}>
                            <TableCell>{r.reason}</TableCell>
                            <TableCell align="right">{r.count}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </Grid>

                <Grid item xs={12} md={7}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                    Most-affected learners
                  </Typography>
                  <Box sx={{ overflowX: 'auto' }}>
                    <Table size="small" sx={{ minWidth: 480 }}>
                      <TableHead><TableRow sx={{ bgcolor: 'grey.100' }}>
                        <TableCell><strong>Learner</strong></TableCell>
                        <TableCell align="center"><strong>Present</strong></TableCell>
                        <TableCell align="center"><strong>Absent</strong></TableCell>
                        <TableCell align="center"><strong>Late</strong></TableCell>
                        <TableCell align="center"><strong>Abs %</strong></TableCell>
                        <TableCell><strong>Top reasons</strong></TableCell>
                      </TableRow></TableHead>
                      <TableBody>
                        {report.learners.filter(l => l.absent + l.late > 0).map(l => (
                          <TableRow key={l.admNo}>
                            <TableCell>{l.name}</TableCell>
                            <TableCell align="center">{l.present}</TableCell>
                            <TableCell align="center" sx={{ color: l.absent ? '#d32f2f' : 'inherit' }}>{l.absent}</TableCell>
                            <TableCell align="center" sx={{ color: l.late ? '#ed6c02' : 'inherit' }}>{l.late}</TableCell>
                            <TableCell align="center">{l.absenceRate}%</TableCell>
                            <TableCell>
                              {Object.entries(l.byReason).sort((a, b) => b[1] - a[1])
                                .map(([r, n]) => <Chip key={r} size="small" label={`${r} ${n}`} sx={{ mr: 0.5, mb: 0.5 }} />)}
                            </TableCell>
                          </TableRow>
                        ))}
                        {report.learners.filter(l => l.absent + l.late > 0).length === 0 && (
                          <TableRow><TableCell colSpan={6}>
                            <Typography color="text.secondary" variant="body2">Full attendance — no absences.</Typography>
                          </TableCell></TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </Box>
                </Grid>
              </Grid>

              {report.byDate.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>By date</Typography>
                  <Box sx={{ overflowX: 'auto' }}>
                    <Table size="small" sx={{ minWidth: 480 }}>
                      <TableHead><TableRow sx={{ bgcolor: 'grey.100' }}>
                        <TableCell><strong>Date</strong></TableCell>
                        <TableCell><strong>Session</strong></TableCell>
                        <TableCell align="center"><strong>Present</strong></TableCell>
                        <TableCell align="center"><strong>Absent</strong></TableCell>
                        <TableCell align="center"><strong>Late</strong></TableCell>
                        <TableCell align="center"><strong>Rate</strong></TableCell>
                      </TableRow></TableHead>
                      <TableBody>
                        {report.byDate.map((d, i) => (
                          <TableRow key={i}>
                            <TableCell>{d.date}</TableCell>
                            <TableCell>{d.session}</TableCell>
                            <TableCell align="center">{d.present}</TableCell>
                            <TableCell align="center">{d.absent}</TableCell>
                            <TableCell align="center">{d.late}</TableCell>
                            <TableCell align="center">{d.rate}%</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                </>
              )}
            </Box>
          ) : null}
        </Paper>
      )}
    </Box>
  );
}
